/** Tipos e rótulos para correções e histórico do realizado financeiro complementar. */

export const RFC_TABLE = "solis_realizado_financeiro_complementar" as const;
export const RFC_AUDIT_TABLE = "solis_realizado_financeiro_complementar_auditoria" as const;
export const RFC_CORRIGIR_RPC = "solis_realizado_fin_complementar_corrigir" as const;

export type RfcEvento = "INSERT" | "UPDATE" | "DELETE";

export type RfcPatchableField =
  | "conta"
  | "tipo"
  | "data"
  | "numero_documento"
  | "nome"
  | "debito"
  | "credito"
  | "saldo"
  | "cadastro_federal_cliente"
  | "cadastro_federal_fornecedor"
  | "memorando"
  | "conta_linha_numero"
  | "subsidiaria_nome"
  | "departamento"
  | "area_tratado"
  | "classe"
  | "sub_area_tratado"
  | "valor"
  | "tipo_receita_despesa"
  | "classificacao_receita_despesa"
  | "descricao_receita_despesa"
  | "nome_arquivo";

export const RFC_FIELD_LABELS: Record<RfcPatchableField, string> = {
  conta: "Conta",
  tipo: "Tipo",
  data: "Data",
  numero_documento: "Número do documento",
  nome: "Nome",
  debito: "Débito",
  credito: "Crédito",
  saldo: "Saldo",
  cadastro_federal_cliente: "Cadastro federal do cliente",
  cadastro_federal_fornecedor: "Cadastro federal do fornecedor",
  memorando: "Memorando",
  conta_linha_numero: "Conta (linha): Número",
  subsidiaria_nome: "Subsidiária: Nome",
  departamento: "Departamento",
  area_tratado: "AREA tratado",
  classe: "Classe",
  sub_area_tratado: "SUB AREA tratado",
  valor: "VALOR",
  tipo_receita_despesa: "Tipo_Receita/Despesa",
  classificacao_receita_despesa: "Classificação_Receita/Despesa",
  descricao_receita_despesa: "Descrição_Receita/Despesa",
  nome_arquivo: "Arquivo de origem",
};

/** Campos mais usados em correções solicitadas pelo financeiro. */
export const RFC_CORRECAO_FIELDS_PRIMARY: RfcPatchableField[] = [
  "data",
  "valor",
  "area_tratado",
  "sub_area_tratado",
  "tipo_receita_despesa",
  "classificacao_receita_despesa",
  "descricao_receita_despesa",
  "debito",
  "credito",
];

export const RFC_CORRECAO_FIELDS_SECONDARY: RfcPatchableField[] = [
  "conta",
  "tipo",
  "numero_documento",
  "nome",
  "saldo",
  "departamento",
  "memorando",
];

/** Canais de solicitação (valor gravado no banco = id). */
export const RFC_CANAIS_SOLICITACAO = [
  { id: "email", label: "E-mail" },
  { id: "reuniao", label: "Reunião" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "telefone", label: "Telefone" },
  { id: "planilha", label: "Planilha / anexo" },
  { id: "ui", label: "Pedido direto na UI" },
  { id: "outro", label: "Outro" },
] as const;

export type RfcCanalSolicitacao = (typeof RFC_CANAIS_SOLICITACAO)[number]["id"];

export function labelCanalSolicitacao(canal: string | null | undefined): string {
  if (!canal) return "—";
  return RFC_CANAIS_SOLICITACAO.find((c) => c.id === canal)?.label ?? canal;
}

export interface RfcAuditoriaRow {
  id: number;
  evento: RfcEvento;
  registro_id: string | null;
  importacao_id: string | null;
  nome_arquivo: string | null;
  ocorrido_em: string;
  origem: string | null;
  motivo: string | null;
  alterado_por: string | null;
  solicitado_por: string | null;
  canal: string | null;
  referencia_solicitacao: string | null;
  observacoes: string | null;
  solicitado_em: string | null;
  linha_resumo: string | null;
  registro_antes: Record<string, unknown> | null;
  registro_depois: Record<string, unknown> | null;
  campos_alterados: Record<string, { antes?: unknown; depois?: unknown }> | null;
}

export interface RfcCorrigirPayload {
  p_id: string;
  p_patch: Record<string, string | number>;
  p_motivo: string;
  p_solicitado_por: string;
  p_canal: string;
  p_referencia_solicitacao?: string;
  p_observacoes: string;
  p_solicitado_em?: string;
}

export interface RfcCorrigirResult {
  success: boolean;
  error?: string;
  id?: string;
}

export function formatRfcValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
  }
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = s.slice(0, 10).split("-");
    if (d.length === 3) return `${d[2]}/${d[1]}/${d[0]}`;
  }
  return s;
}

export function eventoBadgeVariant(evento: RfcEvento): "default" | "secondary" | "destructive" {
  if (evento === "INSERT") return "default";
  if (evento === "UPDATE") return "secondary";
  return "destructive";
}
